import { ContextInput } from 'bernie-context';
import { AnalyticsStore, SerializedData, QueryObject, timeoutPromise } from 'bernie-core';
import { FetchPolicy, ClientOptions } from 'bernie-graphql';
import { getErrorString, Logger } from 'bernie-logger';
import { action, observable, runInAction } from 'mobx';
import { KeyedObject } from 'bernie-core/dist/source';
import { ClientLogger } from 'bernie-client';
import { getTrackingInfoForLoggingFromHeaders } from '../common/logging-utils';
import { ActivityDestinationViewSearch, ActivityGroupViewType } from '../common/__generated__/api';
import { initializeSortAndFilterObject } from '../components/srp/sort-filter';
import { updateSearchCriteriaStore } from '../helpers/activity-search-helper';
import { LXStore, RequestWithTraceInfo } from './lx-store';
import { SearchCriteriaStore } from './search-criteria-store';
import { STORE_EVENTS } from './store-events';

export class ActivitySearchStore extends LXStore {
  @observable public activityGroups: ActivityDestinationViewSearch.ActivityGroups[] = [];
  @observable public destinationViewList: ActivityDestinationViewSearch.ActivityTiles[] = [];
  @observable public listViewActivities: ActivityDestinationViewSearch.ActivityInlineFragment[] = [];
  @observable public loadingActivities?: boolean;
  @observable public isBrowser = true;
  @observable public retainBrowserFocus = false;
  @observable public name?: string;
  @observable public pageInfo: ActivityDestinationViewSearch.PageInfo | null = null;
  @observable public paginationInfo: ActivityDestinationViewSearch.PaginationInfo | null | undefined = null;
  @observable public query: QueryObject = {};
  @observable public regionName?: string;
  @observable public searchError?: boolean;
  @observable public searchSummary: ActivityDestinationViewSearch.SearchSummary | null = null;
  @observable public serverSide?: boolean;
  @observable public sortAndFilter: ActivityDestinationViewSearch.SortAndFilter | null = null;
  @observable public showMoreButton?: boolean;
  @observable public showMoreLoading?: boolean;
  @observable public uisPayload: any;
  @observable public sortAndFilterObject: SerializedData = {};
  @observable public isGridLayoutListViewEnabled = false;
  public firstLoadActivityCount = 0;
  public essDomain?: string;

  public constructor(
    bexConfigs: ClientOptions,
    logger: Logger | ClientLogger,
    appConfig: KeyedObject = {},
    requestOrContext?: ContextInput | RequestWithTraceInfo
  ) {
    super(bexConfigs, logger, appConfig, requestOrContext);
    this.logger = logger;

    if (appConfig) {
      this.firstLoadActivityCount = appConfig.firstLoadActivityCount;
      this.essDomain = appConfig.essDomain;
    }
  }

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
  }

  public searchSetup(): void {
    this.loadingActivities = true;
    this.showMoreLoading = false;
    this.listViewActivities = [];
    this.searchError = false;
    this.activityGroups = [];
    this.showMoreButton = true;
  }

  public clearResponse(): void {
    this.searchSummary = null;
  }

  public resetActivitySearchStore(): void {
    this.clearResponse();
    this.searchSetup();
    this.searchSummary = null;
    this.uisPayload = undefined;
    this.regionName = undefined;
  }

  private getTraceId(): string {
    return (this.headers && this.headers['trace-id']) || null;
  }

  private getMessageId(): string {
    return (this.headers && this.headers['message-id']) || null;
  }

  // for destination view only
  @action
  public fetchActivitiesSearchDestinationView(
    searchCriteriaStore: SearchCriteriaStore,
    analyticsStore: AnalyticsStore,
    query: QueryObject,
    searchVariables: ActivityDestinationViewSearch.Variables | null,
    pageIdentity: string,
    indexOfGroupToUpdate: number | null,
    overrideAllGroups = true,
    shouldUpdateCriteria = true
  ): Promise<any> {
    if (this.logtracing) {
      this.logger.logEvent(
        STORE_EVENTS.DESTINATION_QUERY_FETCHED,
        JSON.stringify(searchVariables),
        `traceid=${this.getTraceId()} messageid=${this.getMessageId()}`
      );
    }

    return timeoutPromise(
      this.bexApiSource.operations
        .activityDestinationViewSearch(searchVariables, {
          pageId: pageIdentity,
          fetchPolicy: FetchPolicy.NO_CACHE,
          traceId: this.getTraceId(),
          messageId: this.getMessageId()
        })
        .then((response: KeyedObject) => {
          runInAction('update bex api activities search from response', () => {
            const activitySearch = response.data.activitySearch;
            const showMoreLoading = false;
            this.destinationViewSearchSuccess(
              activitySearch,
              response.extensions.analytics,
              indexOfGroupToUpdate,
              overrideAllGroups,
              showMoreLoading
            );
          });
          searchCriteriaStore.setGroundTransfersBannerCookie();
          if (shouldUpdateCriteria) {
            updateSearchCriteriaStore(searchCriteriaStore, this, query);
          }
          searchCriteriaStore.isQueryStringUpdated = !shouldUpdateCriteria;
        })
        .then(() => {
          if (this.toPublishPageView()) {
            analyticsStore.resetAnalyticsForNewPage();
            analyticsStore.publishClientSidePayload(this.uisPayload);
          }
        })
        .catch((error: Error) => {
          runInAction('recovering from bex api activities search call breakdown', () => {
            this.searchFailed(searchVariables, error);
          });

          throw error;
        }),
      this.serverSide ? this.appConfig.timeout?.serverStoreFetch : this.appConfig.timeout?.clientStoreFetch,
      `store=search fetch=fetchActivitiesSearchDestinationView timeout ${getTrackingInfoForLoggingFromHeaders(
        this.headers
      )}`
    );
  }

  private searchFailed(searchVariables: ActivityDestinationViewSearch.Variables | null, error: Error): void {
    this.loadingActivities = false;
    this.showMoreLoading = false;
    this.logger.logEvent(STORE_EVENTS.BFF_SEARCH_POOPED, searchVariables, getErrorString(error));
    this.searchError = true;
  }

  private destinationViewSearchSuccess(
    activityDestinationViewSearch: ActivityDestinationViewSearch.ActivitySearch,
    analytics: any,
    indexOfGroupToUpdate: number | null,
    overrideAllGroups: boolean,
    showMoreLoading: boolean
  ): void {
    this.loadingActivities = false;

    if (activityDestinationViewSearch.activityGroups && activityDestinationViewSearch.activityGroups.length > 0) {
      if (overrideAllGroups) {
        this.activityGroups = activityDestinationViewSearch.activityGroups;
      } else {
        const groupWithActivityList = activityDestinationViewSearch.activityGroups.find(
          group => group.viewType === ActivityGroupViewType.ACTIVITY_LIST
        );
        if (groupWithActivityList && indexOfGroupToUpdate !== null) {
          this.activityGroups[indexOfGroupToUpdate].activityTiles.push(...groupWithActivityList.activityTiles);
          this.showMoreButton = groupWithActivityList.action !== null;
        }
      }
    }

    this.showMoreLoading = showMoreLoading;
    this.sortAndFilter = activityDestinationViewSearch.sortAndFilter;
    this.initializeSortFilter();
    this.uisPayload = analytics;
    this.searchSummary = activityDestinationViewSearch.searchSummary;
    this.pageInfo = activityDestinationViewSearch.searchSummary.pageInfo;
    this.paginationInfo = activityDestinationViewSearch.searchSummary.paginationInfo;
    this.regionName =
      (activityDestinationViewSearch.searchSummary.resolvedRegion &&
        activityDestinationViewSearch.searchSummary.resolvedRegion.regionName) ||
      this.query.location;
    this.searchError = false;
  }

  private toPublishPageView(): boolean {
    return !this.showMoreLoading;
  }

  private initializeSortFilter(): void {
    if (!this.sortAndFilter) {
      return;
    }
    this.sortAndFilterObject = initializeSortAndFilterObject(this.sortAndFilter.sortAndFilterSections);
  }
}
